import { LocalTransport } from "../../track/local-transport";

/** Plus grand pas de temps admis par une image d'inertie, en millisecondes (~4 images). */
const MAX_INERTIA_FRAME_MS = 64;

/**
 * ViewNavigator, pure visual offset for scrolling through the piano roll
 * and minimap without moving the LocalTransport during the gesture.
 *
 * Lifecycle of a navigation gesture:
 *
 *   1. beginGesture()     → pause transport, record wasPlaying
 *   2. updateOffset(Δ)    → accumulate viewOffset (ticks) + track velocity
 *   3. endGesture()       → start inertia animation (rAF)
 *      3a. immediate=true → skip inertia, commit instantly (minimap)
 *   4. commit()           → seekToTick(finalTick), reset viewOffset, resume if wasPlaying
 *
 * The effective tick used for rendering is:
 *   localTransport.currentTick() + viewOffset
 */
export class ViewNavigator {
  // ── state ────────────────────────────────────────────────────────
  private _viewOffset = 0;
  private _velocity = 0; // ticks per millisecond
  private _isActive = false;
  private _wasPlaying = false;
  /** La navigation est fermée au joueur. Voir `setLocked`. */
  private _locked = false;
  /** Le tick le plus bas que la vue peut montrer. Voir `setFloorTick`. */
  private _floorTick = 0;

  // ── inertia ──────────────────────────────────────────────────────
  private inertiaRAF: number | null = null;
  private lastInertiaTime = 0;

  /** Friction coefficient applied every frame (0–1, lower = more friction) */
  private readonly FRICTION = 0.94;
  /** Velocity threshold below which inertia stops (ticks/ms) */
  private readonly VELOCITY_THRESHOLD = 0.05;

  // ── velocity tracking (smoothed) ─────────────────────────────────
  /** Circular buffer of recent (timestamp, deltaTicks) samples */
  private velocitySamples: { t: number; d: number }[] = [];
  /** Max age of samples used to compute velocity (ms) */
  private readonly VELOCITY_WINDOW = 100;

  // ── scroll-to animation (linear ease, adaptive duration) ─────────
  private scrollToRAF: number | null = null;
  private scrollToResolve: (() => void) | null = null;
  /** Target offset for the current segment. */
  private scrollToTarget = 0;
  /**
   * Le tick visé, en absolu, ou `null` hors défilement.
   *
   * L'arrivée se calculait en **ajoutant** le décalage visuel à la position du
   * transport. Tant que rien ne bouge le transport pendant le vol, les deux
   * disent la même chose ; dès que quelque chose le déplace, l'arrivée passe la
   * cible d'autant. C'est arrivé deux fois au parcours harmonique, par deux
   * appelants différents, et ça continuera d'arriver : un vol dure une mesure
   * entière, et personne ne peut promettre que rien ne se posera entre-temps.
   *
   * On garde donc la cible telle qu'elle a été demandée. Le geste du joueur,
   * lui, n'en a pas — il ne vise rien, il pousse — et son commit reste relatif.
   */
  private scrollToAbsolute: number | null = null;
  /** viewOffset at the start of the current segment. */
  private scrollToStartOffset = 0;
  /** Timestamp at the start of the current segment. */
  private scrollToStartTime = 0;
  /** Duration of the current segment (ms), computed from distance. */
  private scrollToDuration = 0;

  /**
   * Linear ease bounds. Each retarget starts a fresh segment whose
   * duration scales with the remaining distance:
   *   duration = clamp(|distance| / SPEED, MIN, MAX)
   *
   * Rationale: a critically-damped spring has a velocity hump that gets
   * interrupted on rapid retargets (note-by-note in the learning journey),
   * producing visible saccades. Linear motion has constant velocity within
   * a segment, chained segments at a steady note rate give a near-uniform
   * scroll speed with zero accumulated lag.
   */
  private readonly SCROLL_TO_DURATION_MIN = 80;
  private readonly SCROLL_TO_DURATION_MAX = 400;
  /** Reference scroll speed in ticks/ms, controls how distance maps to duration. */
  private readonly SCROLL_TO_SPEED = 4;

  // ── wheel debounce ───────────────────────────────────────────────
  private wheelDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly WHEEL_DEBOUNCE_MS = 150;
  private wheelGestureActive = false;

  // ── scrub listeners ──────────────────────────────────────────────
  /**
   * Notified when the **player** has moved the view and let go. Not when the
   * application scrolls the roll itself: `animateScrollToTick` commits
   * through `commitScrollTo`, which is silent on purpose. The learning
   * journey scrolls the roll on every attack of a run, so a single set of
   * listeners for both would fire dozens of times per pass and could never
   * tell "the player chose a place" from "the step moved the view".
   */
  private scrubListeners = new Set<(tick: number) => void>();

  constructor(private localTransport: LocalTransport) {}

  // ── public API ───────────────────────────────────────────────────

  /**
   * Subscribe to the end of a navigation gesture: drag, wheel, or a click on
   * the minimap. The tick is where the view came to rest. Returns the
   * unsubscribe function.
   */
  onScrub(listener: (tick: number) => void): () => void {
    this.scrubListeners.add(listener);

    return () => {
      this.scrubListeners.delete(listener);
    };
  }

  /** Current visual tick = transport tick + offset. Used by renderers. */
  getEffectiveTick(): number {
    return Math.max(this._floorTick, this.localTransport.currentTick() + this._viewOffset);
  }

  /**
   * Jusqu'où la vue peut remonter, en ticks du morceau courant.
   *
   * Zéro, le début du morceau, depuis toujours : on ne défile pas avant la
   * première mesure. Une séquence de morceaux descend ce plancher au début du
   * **premier** morceau, en ticks relatifs au courant — négatifs, donc — pour
   * que remonter à la molette ou depuis la minimap puisse traverser une
   * frontière vers l'arrière, comme la lecture la traverse vers l'avant. Le
   * transport, lui, ne va pas sous zéro : c'est sa borne (`setBound`) qui
   * reçoit la destination négative et la remet dans le morceau d'avant.
   */
  setFloorTick(tick: number): void {
    this._floorTick = tick;
  }

  get floorTick(): number {
    return this._floorTick;
  }

  /**
   * Placer la tête sans arracher la vue à qui la porte.
   *
   * Un vol dure aussi longtemps que ce qu'il traverse — une mesure entière au
   * parcours harmonique — et pendant tout ce temps le transport ne bouge pas :
   * c'est le décalage visuel qui avance. Quiconque déplace le transport
   * pendant ce temps-là fait donc sauter l'image de tout le décalage en cours,
   * puisque la position dessinée est la somme des deux.
   *
   * Le décalage absorbe donc le déplacement, et l'image ne bouge pas d'un
   * pixel. Le vol, lui, continue et atterrit là où il visait : sa cible est
   * absolue, pas relative à un transport qui aurait pu bouger.
   *
   * Hors vol et hors geste il n'y a rien à absorber : un décalage nul le
   * resterait, et lui donner une valeur ferait dériver la vue de son transport.
   */
  seekWithoutJump(tick: number): void {
    if (this.scrollToRAF !== null || this._isActive) {
      const delta = tick - this.localTransport.currentTick();

      this._viewOffset -= delta;
      this.scrollToStartOffset -= delta;
      this.scrollToTarget -= delta;
    }

    this.localTransport.seekToTick(tick);
  }

  /** Raw offset in ticks (for minimap absolute positioning). */
  get viewOffset(): number {
    return this._viewOffset;
  }

  /** Whether a navigation gesture is currently active. */
  get isActive(): boolean {
    return this._isActive;
  }

  /** La navigation est-elle fermée au joueur. */
  get isLocked(): boolean {
    return this._locked;
  }

  /**
   * Fermer la navigation au joueur, ou la lui rendre.
   *
   * # Pourquoi ici et pas chez les surfaces
   *
   * Le rouleau, la minimap, la molette et une partition React conduisent tous
   * le même geste par les mêmes quatre verbes — c'est le seul objet qu'ils
   * touchent. Un spectateur de récital ne doit pouvoir déplacer la vue par
   * aucun d'eux : la fermer une fois, à l'entonnoir, vaut mieux qu'un drapeau
   * à recopier chez chaque surface, et chez celles qu'on ajoutera.
   *
   * # Ce que le verrou ne ferme pas
   *
   * Les déplacements que l'application fait elle-même — `seekWithoutJump`,
   * `animateScrollToTick` — ne sont pas des gestes : ils restent ouverts.
   * C'est ce qui laisse la vue suivre le transport partagé, qui est tout ce
   * qu'un spectateur regarde.
   *
   * # Un geste en cours au moment du verrou
   *
   * Il se termine comme si le joueur venait de lâcher : la vue est posée où
   * elle en était, et le transport reprend s'il tournait. Le laisser à moitié
   * fait garderait un décalage que rien ne viendrait plus résorber, et un
   * transport décroché que personne ne raccrocherait.
   */
  setLocked(locked: boolean): void {
    if (locked === this._locked) {
      return;
    }

    this._locked = locked;

    if (!locked) {
      return;
    }

    if (this.wheelDebounceTimer !== null) {
      clearTimeout(this.wheelDebounceTimer);
      this.wheelDebounceTimer = null;
    }

    this.wheelGestureActive = false;

    if (this._isActive || this.inertiaRAF !== null) {
      this._isActive = false;
      this.commit();
    }
  }

  /**
   * Whether any navigator activity requires the renderer to keep drawing: a
   * live gesture, a pending wheel gesture, an inertia glide, or a scroll-to
   * animation in flight. The render governor polls this to keep the Pixi ticker
   * awake; when it goes false (and the transport is paused) the ticker parks.
   */
  get isBusy(): boolean {
    return (
      this._isActive ||
      this.wheelGestureActive ||
      this.inertiaRAF !== null ||
      this.scrollToRAF !== null
    );
  }

  // ── gesture lifecycle ────────────────────────────────────────────

  /**
   * Call when the user starts a drag / touch / first wheel event.
   * Pauses the transport so notes don't play during scrolling.
   */
  beginGesture(): void {
    // Verrouillé, aucun geste ne commence — et comme rien ne commence, les
    // trois verbes qui suivent n'ont rien à continuer ni à finir. Chacun se
    // garde quand même : `updateOffset` ne vérifie pas qu'un geste est en
    // cours, et la minimap l'appelle d'un seul clic.
    if (this._locked || this._isActive) {
      return;
    }

    this._isActive = true;

    // If a scroll-to animation was running, cancel it but keep the
    // current viewOffset so the gesture continues from the visual position.
    const wasScrollToRunning = this.scrollToRAF !== null;

    if (wasScrollToRunning) {
      this.cancelScrollTo();
    }

    // If inertia was running from a previous gesture, cancel it but
    // preserve _wasPlaying, the transport is already paused and the
    // original play state is still valid.
    const wasInertiaRunning = this.inertiaRAF !== null;
    this.cancelInertia();

    if (!wasInertiaRunning && !wasScrollToRunning) {
      // Fresh gesture: capture the current transport state
      this._wasPlaying = !this.localTransport.isDetached;

      if (this._wasPlaying) {
        this.localTransport.detach();
      }
    }
    // else: transport already paused by previous gesture/scroll-to, _wasPlaying already set

    this.velocitySamples.length = 0;
    this._velocity = 0;
  }

  /**
   * Call during the gesture to accumulate offset.
   * @param deltaTicks, signed tick delta (positive = forward in time = scroll down)
   */
  updateOffset(deltaTicks: number): void {
    if (this._locked) {
      return;
    }

    this._viewOffset += deltaTicks;
    this.clampOffset();

    // Record sample for velocity computation
    const now = performance.now();
    this.velocitySamples.push({ t: now, d: deltaTicks });

    // Prune old samples
    while (
      this.velocitySamples.length > 0 &&
      now - this.velocitySamples[0].t > this.VELOCITY_WINDOW
    ) {
      this.velocitySamples.shift();
    }
  }

  /**
   * Call when the gesture ends (pointer up, or wheel debounce fires).
   * @param immediate, if true, skip inertia and commit now (used by minimap)
   */
  endGesture(immediate = false): void {
    if (this._locked || !this._isActive) {
      return;
    }

    this._isActive = false;

    if (immediate) {
      this.commit();

      return;
    }

    // Compute average velocity from recent samples
    this._velocity = this.computeVelocity();

    if (Math.abs(this._velocity) < this.VELOCITY_THRESHOLD) {
      this.commit();

      return;
    }

    // Start inertia animation
    this.lastInertiaTime = performance.now();
    this.inertiaRAF = requestAnimationFrame(this.animateInertia);
  }

  /**
   * Convenience for wheel events: auto-manages beginGesture / endGesture
   * with debounce, so the caller only needs to call this on each wheel event.
   */
  handleWheelDelta(deltaTicks: number): void {
    if (this._locked) {
      return;
    }

    if (!this.wheelGestureActive) {
      this.wheelGestureActive = true;
      this.beginGesture();
    }

    this.updateOffset(deltaTicks);

    // Reset debounce timer
    if (this.wheelDebounceTimer !== null) {
      clearTimeout(this.wheelDebounceTimer);
    }

    this.wheelDebounceTimer = setTimeout(() => {
      this.wheelDebounceTimer = null;
      this.wheelGestureActive = false;
      this.endGesture();
    }, this.WHEEL_DEBOUNCE_MS);
  }

  /** Reset to zero offset, cancel any inertia or scroll-to animation. */
  reset(): void {
    this.cancelInertia();
    this.cancelScrollTo();
    this._viewOffset = 0;
    this._velocity = 0;
    this._isActive = false;
    this.wheelGestureActive = false;

    if (this.wheelDebounceTimer !== null) {
      clearTimeout(this.wheelDebounceTimer);
      this.wheelDebounceTimer = null;
    }
  }


  destroy(): void {
    this.reset();
    this.scrubListeners.clear();
  }

  // ── scroll-to animation (public API) ─────────────────────────────

  /**
   * Smoothly animate to a target tick using a linear ease with
   * distance-adaptive duration.
   *
   * - Pauses the transport for the duration of the animation.
   * - Resolves when the animation completes and the transport has been
   *   seeked to the target tick.
   * - Calling again while an animation is running starts a fresh linear
   *   segment from the current visual position toward the new target.
   *   Across rapid retargets at a steady rate, segment velocities chain
   *   into a near-uniform scroll with zero accumulated lag.
   * - Any user gesture (drag / wheel) cancels the animation immediately.
   */
  animateScrollToTick(targetTick: number, durationMs?: number): Promise<void> {
    const wasAnimating = this.scrollToRAF !== null;

    if (this.inertiaRAF !== null) {
      this.cancelInertia();
    }

    if (!wasAnimating && !this._isActive) {
      this._wasPlaying = !this.localTransport.isDetached;

      if (this._wasPlaying) {
        this.localTransport.detach();
      }
    }

    // Start a fresh segment from the current visual position toward the
    // new target. Duration scales with the remaining distance (clamped).
    const currentTransportTick = this.localTransport.currentTick();
    const target = targetTick - currentTransportTick;
    const distance = Math.abs(target - this._viewOffset);

    this.scrollToStartOffset = this._viewOffset;
    this.scrollToTarget = target;
    this.scrollToAbsolute = Math.max(this._floorTick, targetTick);
    this.scrollToStartTime = performance.now();
    // Une durée imposée l'emporte sur celle que la distance donnerait. Le
    // parcours harmonique s'en sert : entre deux frappes, la tête doit mettre
    // exactement ce que le morceau met, et non ce qu'un défilement confortable
    // mettrait. Sans elle, une porte à une noire de la suivante était franchie
    // en 400 ms puis l'écran attendait, ce qui est l'inverse d'un repère de
    // rythme. Les appelants qui n'en passent pas gardent leur cadence.
    this.scrollToDuration =
      durationMs !== undefined && durationMs > 0
        ? durationMs
        : Math.min(
            this.SCROLL_TO_DURATION_MAX,
            Math.max(this.SCROLL_TO_DURATION_MIN, distance / this.SCROLL_TO_SPEED),
          );

    // Resolve any previous promise, the caller now waits on the new target.
    this.scrollToResolve?.();

    return new Promise<void>((resolve) => {
      this.scrollToResolve = resolve;

      if (!wasAnimating) {
        this.scrollToRAF = requestAnimationFrame(this.animateScrollToFrame);
      }
    });
  }

  // ── scroll-to animation (rAF loop) ─────────────────────────────

  /**
   * Per-frame update: linear interpolation from the segment's start offset
   * toward its target over `scrollToDuration` ms. On retarget, a new
   * segment is started from the current position.
   */
  private animateScrollToFrame = (): void => {
    const t = performance.now() - this.scrollToStartTime;
    const u = Math.min(t / this.scrollToDuration, 1);

    this._viewOffset =
      this.scrollToStartOffset +
      u * (this.scrollToTarget - this.scrollToStartOffset);

    if (u >= 1) {
      this._viewOffset = this.scrollToTarget;
      this.commitScrollTo();

      return;
    }

    this.scrollToRAF = requestAnimationFrame(this.animateScrollToFrame);
  };

  /** Commit scroll-to: seek transport, resolve promise, cleanup. */
  private commitScrollTo(): void {
    this.cancelScrollToRAF();

    const finalTick =
      this.scrollToAbsolute
      ?? Math.max(this._floorTick, this.localTransport.currentTick() + this._viewOffset);

    this.scrollToAbsolute = null;
    this._viewOffset = 0;

    this.localTransport.seekToTick(finalTick);

    if (this._wasPlaying) {
      this.localTransport.play();
      this._wasPlaying = false;
    }

    const resolve = this.scrollToResolve;
    this.scrollToResolve = null;
    resolve?.();
  }

  /** Cancel only the scroll-to rAF (does not resolve the promise). */
  private cancelScrollToRAF(): void {
    if (this.scrollToRAF !== null) {
      cancelAnimationFrame(this.scrollToRAF);
      this.scrollToRAF = null;
    }
  }

  /**
   * Cancel a running scroll-to animation entirely.
   * The viewOffset is preserved so a gesture can continue from here.
   */
  private cancelScrollTo(): void {
    this.cancelScrollToRAF();
    const resolve = this.scrollToResolve;
    this.scrollToResolve = null;
    resolve?.();
  }

  // ── inertia animation ────────────────────────────────────────────

  private animateInertia = (): void => {
    const now = performance.now();
    // Borné : au retour d'un onglet masqué en plein glissé, l'écart réel se
    // compte en secondes et téléporterait la vue avant que la friction n'agisse.
    const dt = Math.min(MAX_INERTIA_FRAME_MS, now - this.lastInertiaTime);
    this.lastInertiaTime = now;

    // Apply velocity
    this._viewOffset += this._velocity * dt;

    // Apply friction
    this._velocity *= this.FRICTION;

    // Hitting the start of the piece stops inertia dead: never drift before
    // tick 0, and don't let the offset accumulate into the void (which would
    // leave a dead zone when scrolling back forward).
    const minOffset = this.minOffset();

    if (this._viewOffset <= minOffset) {
      this._viewOffset = minOffset;
      this.commit();

      return;
    }

    // Check if we should stop
    if (Math.abs(this._velocity) < this.VELOCITY_THRESHOLD) {
      this.commit();

      return;
    }

    this.inertiaRAF = requestAnimationFrame(this.animateInertia);
  };

  // ── internals ────────────────────────────────────────────────────

  /**
   * Lowest offset that keeps the effective tick at or above the floor: the
   * view can never scroll before the start of the piece — or of the sequence,
   * see `setFloorTick`. The transport is paused during gestures / animations,
   * so `currentTick()` is stable across the clamp.
   */
  private minOffset(): number {
    return this._floorTick - this.localTransport.currentTick();
  }

  /** Floor the accumulated offset so the effective tick never goes below zero. */
  private clampOffset(): void {
    const min = this.minOffset();

    if (this._viewOffset < min) {
      this._viewOffset = min;
    }
  }

  /**
   * Commit the current visual offset to the transport:
   * seek to the effective tick, reset offset, resume playback if needed.
   */
  private commit(): void {
    this.cancelInertia();

    const finalTick = Math.max(this._floorTick, this.localTransport.currentTick() + this._viewOffset);
    this._viewOffset = 0;
    this._velocity = 0;

    this.localTransport.seekToTick(finalTick);

    if (this._wasPlaying) {
      this.localTransport.play();
      this._wasPlaying = false;
    }

    // Only here, and never in `commitScrollTo`: this is the path a gesture
    // ends on, so a listener woken by it knows the player chose this place.
    this.scrubListeners.forEach((listener) => listener(finalTick));
  }

  private cancelInertia(): void {
    if (this.inertiaRAF !== null) {
      cancelAnimationFrame(this.inertiaRAF);
      this.inertiaRAF = null;
    }
  }

  /**
   * Compute smoothed velocity from recent samples (ticks / ms).
   */
  private computeVelocity(): number {
    if (this.velocitySamples.length < 2) {
      return 0;
    }

    const now = performance.now();
    // Only consider samples within the velocity window
    const recent = this.velocitySamples.filter(
      (s) => now - s.t <= this.VELOCITY_WINDOW,
    );

    if (recent.length < 2) {
      return 0;
    }

    const totalDelta = recent.reduce((sum, s) => sum + s.d, 0);
    const totalTime = recent[recent.length - 1].t - recent[0].t;

    if (totalTime <= 0) {
      return 0;
    }

    return totalDelta / totalTime;
  }
}
